/**
 * SheetCTAFooter
 *
 * Sticky-bottom chrome for a sheet's CTA row: pins the row to the bottom of the
 * scrolling sheet content and carries the sheet's bottom corner radii so the row
 * doesn't square off the container's rounded edge.
 *
 * This is a *pure wrapper* — it renders exactly one `<div>` and nothing else, so
 * repointing a hand-rolled call site at it leaves the rendered DOM byte-identical.
 * It deliberately does not render `CTAButtons` itself: call sites pass different
 * button configs and own the `closeOnClick` / `onClose` contract, which is
 * `CTAButtons`' concern rather than the footer chrome's.
 *
 * Host requirement: the sheet content must scroll within a fixed-height
 * container for `position: sticky` to have an effect.
 */
import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';

export interface SheetCTAFooterProps {
  /**
   * `bottom` offset for the sticky row. Defaults to `0`; pass `-1` to overlap a
   * trailing edge that would otherwise show through beneath the footer (e.g. a
   * VirtualList's last row — SendGiftSheetContent's case).
   */
  bottomOffset?: number;
  children: React.ReactNode;
}

export const SheetCTAFooter: React.FC<SheetCTAFooterProps> = ({
  bottomOffset = 0,
  children,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  return (
    <div
      style={{
        position: 'sticky',
        bottom: bottomOffset,
        backgroundColor: theme.modalBackground,
        borderBottomLeftRadius: responsive.dialog.borderRadius,
        borderBottomRightRadius: responsive.dialog.borderRadius,
      }}
    >
      {children}
    </div>
  );
};
